import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Clock, Send, User, Phone, Mail, XCircle, CheckCircle2 } from "lucide-react";
import { getBookingAccess, cancelBookingAccess, sendBookingMessage, markBookingAccessRead, unreadCount } from "../api";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Ein Fehler ist aufgetreten.";

const CustomerBooking = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [err, setErr] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const threadRef = useRef(null);

  const load = () =>
    getBookingAccess(token)
      .then((d) => { setData(d); setState("ready"); if (unreadCount(d.messages, d.read_at, "customer") > 0) markBookingAccessRead(token).catch(() => {}); })
      .catch((e) => { setErr(errMsg(e)); setState("invalid"); });

  useEffect(() => { load(); }, [token]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [data?.messages?.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try { const r = await sendBookingMessage(token, t); setData({ ...data, messages: r.messages }); setText(""); }
    catch (e) { setErr(errMsg(e)); }
    setSending(false);
  };

  const cancel = async () => {
    if (!window.confirm("Möchten Sie diese Buchung wirklich absagen? Ihre Putzfrau wird automatisch informiert.")) return;
    setCancelling(true);
    try { await cancelBookingAccess(token); await load(); }
    catch (e) { setErr(errMsg(e)); }
    setCancelling(false);
  };

  if (state === "loading") return <div className="pt-32 pb-20 text-center text-slate-400">Lädt…</div>;
  if (state === "invalid")
    return (
      <div className="max-w-md mx-auto px-5 pt-32 pb-20">
        <div data-testid="cb-invalid" className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-6 text-center">
          {err || "Dieser Link ist ungültig."}
        </div>
      </div>
    );

  const cl = data.cleaner;
  return (
    <div className="max-w-2xl mx-auto px-5 pt-28 md:pt-32 pb-20" data-testid="cb-page">
      <h1 className="font-head font-extrabold text-3xl text-slate-900 mb-1">Ihre Buchung</h1>
      <p className="text-slate-500 mb-6">Hallo {data.name}, hier verwalten Sie Ihren Reinigungsauftrag.</p>

      {data.cancelled && (
        <div data-testid="cb-cancelled-banner" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4 mb-5 font-medium">
          <XCircle className="w-5 h-5" /> Diese Buchung wurde abgesagt.
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-5">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
          <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900"><MapPin className="w-4 h-4 text-teal-600" /> {data.city}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {data.hours} Std.{data.freq ? ` · ${data.freq}` : ""}</span>
          {data.first_visit_date && <span className="inline-flex items-center gap-1.5 text-teal-700 font-medium"><CheckCircle2 className="w-4 h-4" /> 1. Einsatz: {data.first_visit_date}</span>}
        </div>
        {cl ? (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs font-semibold text-slate-400 uppercase mb-2">Ihre Putzfrau</div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
              <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900"><User className="w-4 h-4 text-teal-600" /> {cl.name}</span>
              {cl.phone && <a href={`tel:${cl.phone}`} className="inline-flex items-center gap-1.5 text-teal-700 hover:underline"><Phone className="w-4 h-4" /> {cl.phone}</a>}
              {cl.email && <a href={`mailto:${cl.email}`} className="inline-flex items-center gap-1.5 text-teal-700 hover:underline"><Mail className="w-4 h-4" /> {cl.email}</a>}
            </div>
            {!data.first_visit_date && !data.cancelled && (
              <div data-testid="cb-contact-hint" className="mt-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm font-medium">
                Bitte nehmen Sie Kontakt mit {cl.name} auf, um den ersten Einsatztag zu vereinbaren.
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">Wir suchen gerade Ihre passende Putzfrau – Sie hören bald von uns.</div>
        )}
      </div>

      {/* Chat */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          Nachrichten {cl ? `mit ${cl.name}` : ""}
          {(() => { const u = unreadCount(data.messages, data.read_at, "customer"); return u > 0 ? <span data-testid="cb-unread" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">{u}</span> : null; })()}
        </div>
        <div data-testid="cb-thread" ref={threadRef} className="p-5 space-y-3 max-h-[46vh] overflow-y-auto bg-slate-50/50">
          {(!data.messages || data.messages.length === 0) && (
            <div className="text-center text-slate-400 text-sm py-6">Noch keine Nachrichten. Schreiben Sie Ihrer Putzfrau z. B. Ihre Wunsch-Uhrzeit.</div>
          )}
          {(data.messages || []).map((m) => (
            <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === "customer" ? "bg-teal-700 text-white rounded-br-sm" : m.sender === "support" ? "bg-slate-900 text-white rounded-bl-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
                <div className={`text-[11px] mb-0.5 ${m.sender === "customer" ? "text-teal-100" : m.sender === "support" ? "text-slate-300" : "text-slate-400"}`}>{m.sender === "customer" ? "Sie" : m.sender === "support" ? "MyCleaner Support" : (cl?.name || "Putzfrau")}</div>
                {m.text}
              </div>
            </div>
          ))}
        </div>
        {!data.cancelled && (
          <div className="p-3 border-t border-slate-100 flex items-center gap-2">
            <input
              data-testid="cb-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Nachricht schreiben…"
              className="flex-1 h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
            <button data-testid="cb-message-send" disabled={sending || !text.trim()} onClick={send} className="inline-flex items-center gap-1.5 px-4 h-11 rounded-xl bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 disabled:opacity-50">
              <Send className="w-4 h-4" /> Senden
            </button>
          </div>
        )}
      </div>

      {err && <div data-testid="cb-error" className="text-sm text-red-600 mb-4">{err}</div>}

      {!data.cancelled && (
        <button data-testid="cb-cancel" disabled={cancelling} onClick={cancel} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-red-600 font-medium disabled:opacity-50">
          <XCircle className="w-4 h-4" /> {cancelling ? "Wird abgesagt…" : "Buchung absagen"}
        </button>
      )}
    </div>
  );
};

export default CustomerBooking;
